import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Divider,
  Card,
  CardContent,
  Button
} from '@mui/material';

interface AdPreview {
  prompt: string;
  response: string;
  ad: {
    title: string;
    description: string;
    cta: string;
  };
  placement: 'before' | 'inline' | 'after' | 'recommendation';
}

interface LLMResponseRendererProps {
  previewData: AdPreview;
}

export const LLMResponseRenderer: React.FC<LLMResponseRendererProps> = ({ previewData }) => {
  const renderAd = () => (
    <Card sx={{ 
      backgroundColor: '#0F172A', 
      borderRadius: 2,
      mb: 2,
      border: '1px solid rgba(59, 130, 246, 0.2)'
    }}>
      <CardContent>
        <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
          {previewData.ad.title}
        </Typography>
        <Typography sx={{ color: '#94A3B8', mb: 2 }}>
          {previewData.ad.description}
        </Typography>
        <Button 
          variant="contained" 
          size="small"
          sx={{ 
            backgroundColor: '#3B82F6',
            '&:hover': { backgroundColor: '#2563EB' },
          }}
        >
          {previewData.ad.cta}
        </Button>
      </CardContent>
    </Card>
  );

  const renderContent = () => {
    switch (previewData.placement) {
      case 'before':
        return (
          <>
            {renderAd()}
            <Divider sx={{ my: 2, borderColor: 'rgba(148, 163, 184, 0.2)' }} />
            <Typography sx={{ color: '#E2E8F0', whiteSpace: 'pre-wrap' }}>
              {previewData.response}
            </Typography>
          </>
        );
      case 'inline':
        const parts = previewData.response.split('\n\n');
        return (
          <>
            <Typography sx={{ color: '#E2E8F0', whiteSpace: 'pre-wrap' }}>
              {parts[0]}
            </Typography>
            {renderAd()}
            <Typography sx={{ color: '#E2E8F0', whiteSpace: 'pre-wrap' }}>
              {parts.slice(1).join('\n\n')}
            </Typography>
          </>
        );
      case 'after':
        return (
          <>
            <Typography sx={{ color: '#E2E8F0', whiteSpace: 'pre-wrap' }}>
              {previewData.response}
            </Typography>
            <Divider sx={{ my: 2, borderColor: 'rgba(148, 163, 184, 0.2)' }} />
            {renderAd()}
          </>
        );
      case 'recommendation':
        return (
          <>
            <Typography sx={{ color: '#E2E8F0', whiteSpace: 'pre-wrap' }}>
              {previewData.response}
            </Typography>
            <Divider sx={{ my: 2, borderColor: 'rgba(148, 163, 184, 0.2)' }} />
            <Typography variant="subtitle1" sx={{ color: '#94A3B8', mb: 2 }}>
              Recommended for you:
            </Typography>
            {renderAd()}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 3, 
        backgroundColor: '#1E293B',
        borderRadius: 2
      }}
    >
      <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
        {previewData.prompt}
      </Typography>
      {renderContent()}
    </Paper>
  );
}; 